import React, { FC } from "react";

/**
 * Panel glyph set — Lucide geometry, inline, `currentColor` only.
 *
 * Deliberately NOT `utils/icons.tsx`: those take a `stroke` PROP that defaults
 * to a literal (`IconDownload` is `#B0B0B0`), which is exactly why the current
 * per-thumbnail download control is invisible on a white plate. Everything here
 * inherits its colour from the element it sits in, so a token on the parent is
 * the only thing that ever decides it.
 */
const svg =
  (inner: string, fill = false): FC<{ size?: number; strokeWidth?: number }> =>
  ({ size = 16, strokeWidth = 1.8 }) =>
    (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill={fill ? "currentColor" : "none"}
        stroke={fill ? "none" : "currentColor"}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: inner }}
      />
    );

export const IconDownloadGlyph = svg(
  '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>',
);

export const IconImageGlyph = svg(
  '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>',
);

export const IconFileTextGlyph = svg(
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>',
);

export const IconStoryGlyph = svg(
  '<rect x="3" y="5" width="14" height="14" rx="2"/><path d="M17 8h2a2 2 0 0 1 2 2v9"/>',
);

export const IconChartGlyph = svg(
  '<path d="M3 3v18h18"/><polyline points="7 15 11 11 14 14 20 8"/>',
);

export const IconAlertGlyph = svg(
  '<path d="M12 4 2.5 20h19z"/><path d="M12 10v4"/><circle cx="12" cy="17.2" r=".9" fill="currentColor" stroke="none"/>',
);

export const IconChevronDownGlyph = svg('<polyline points="6 9 12 15 18 9"/>');

/* Performance stat-tile glyphs — replace the four raster PNGs
   (eye/time/play/thumbsUp .png) that could not take a theme. */

export const IconThumbsUpGlyph = svg(
  '<path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>',
);

export const IconEyeGlyph = svg(
  '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
);

export const IconClockGlyph = svg(
  '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
);

export const IconPlayGlyph = svg('<polygon points="6 4 19 12 6 20"/>');

/* ---- Download-menu glyphs, one per file type (handoff §09) ---- */
export const IconFilmGlyph = svg(
  '<rect x="2" y="6" width="14" height="12" rx="2"/><path d="m17 10 5-2v8l-5-2z"/>',
);
export const IconZapGlyph = svg('<path d="M13 2 3 14h7l-1 8 10-12h-7z"/>');
export const IconCirclePlusGlyph = svg('<circle cx="12" cy="12" r="9"/><path d="M8 12h8M12 8v8"/>');
export const IconShieldGlyph = svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>');
export const IconFolderDownGlyph = svg(
  '<path d="M21 8v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="16"/><polyline points="9.5 13.5 12 16 14.5 13.5"/>',
);

/** Link glyph — the page-score footer's URL line. */
export const IconLinkGlyph = svg(
  '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
);
export const IconChevronRightGlyph = svg('<polyline points="9 18 15 12 9 6"/>');
/** Rotate-cw — Retry / Check again. */
export const IconRefreshGlyph = svg(
  '<path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 3 21 9 15 9"/>',
);

/** External link — the page-score panel's link out to the live PDP. */
export const IconGoToTabGlyph = svg(
  '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>',
);
